import get from 'lodash/get';
import moment from 'moment-timezone';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import getInitialConfiguration from '@src/api/initial';
import { useInitialInfoStore } from '@src/emp-3.0/store/useInitialInfo';
import { selector as commonSelector } from '@store/common';
import { updateInitialConfiguration, updateInterval } from '@store/common/actions';
import { calcShortcutInterval } from '@utils';
import { DATE_SHORTCUTS } from '@utils/constants';
import { getEventListForCreateMetric, getValidTimeRange } from '@src/emp-3.0/api/queries';
import { transformGroupByResToList } from '@src/emp-3.0/utils/mappedEvents/transformGroupByResToList';
import { CAN_BE_MAPPED_METADATA_DIMENSION_TYPE } from '@src/components/mapping-panel/constants/constant';
import { getDimensionList } from '@src/api/common/query';
import { EDataSourceType, ESystemType } from '@src/emp-3.0/types';
import { defaultCalenderBoundry } from '@src/emp-3.0/components/Sankey/constants';
import { calcShortcutIntervalWithinBoundary } from '@src/utils/calcShortcutInterval';

const setMomentDefaultTimezone = (timezone) => moment.tz.setDefault(timezone);

const initialConfiguration = async () => {
  const resp = await getInitialConfiguration();
  return resp.data;
};

const getCalenderBoundry = async (systemType) => {
  const res = await getValidTimeRange(systemType);
  if (res?.data?.data) {
    return res.data.data;
  }
  return defaultCalenderBoundry;
};

const initialMetadata = async () => {
  const res = await getDimensionList();
  const metadataList = (res?.data?.data ?? []).filter(
    (dimension) =>
      dimension?.can_be_mapped &&
      CAN_BE_MAPPED_METADATA_DIMENSION_TYPE.includes(dimension?.dimension_type),
  );
  return metadataList;
};

export const initialPredefinedMappedEvents = async () => {
  const [from, to] = calcShortcutIntervalWithinBoundary(DATE_SHORTCUTS.last7Days, ESystemType.EMP);
  const defaultInterval = `${from.format()}/${to.format()}`;
  const res = await getEventListForCreateMetric(defaultInterval);
  const eventList = transformGroupByResToList(res?.data?.data);
  return eventList.filter((event) => event.is_predefined === 1);
};

export default (initialCheckEnable = true) => {
  if (!initialCheckEnable) {
    return [];
  }
  const {
    setUserInfo,
    setPredefinedMappedEvents,
    setMetadata,
    setEmpCalenderBoundry,
    setTrendsCalenderBoundry,
  } = useInitialInfoStore();

  const { interval } = useSelector(commonSelector);
  const [error, setError] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const [customerEnabled, setCustomerEnabled] = useState(false);
  const [username, setUsername] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const dispatch = useDispatch();

  useEffect(() => {
    Promise.all([
      initialConfiguration(),
      getCalenderBoundry(ESystemType.EMP),
      getCalenderBoundry(ESystemType.Trends),
    ])
      .then(([initialConfiguration, empCalenderBoundry, trendsCalenderBoundry]) => {
        dispatch(updateInitialConfiguration({ ...initialConfiguration?.data }));
        // store user info to zustand.
        setUserInfo({ ...initialConfiguration?.data });
        setMomentDefaultTimezone(initialConfiguration?.data?.tzName);
        setCustomerEnabled(get(initialConfiguration.data, 'customer_enabled'));
        setUsername(get(initialConfiguration.data, 'username', ''));

        // Batch mode
        if (initialConfiguration?.data?.data_source_type === EDataSourceType.BATCH_MODE) {
          // set calendar boundaries from api response
          setEmpCalenderBoundry(empCalenderBoundry);
          setTrendsCalenderBoundry(trendsCalenderBoundry);
          const [from, to] = calcShortcutIntervalWithinBoundary(
            DATE_SHORTCUTS.past24Hours,
            ESystemType.EMP,
          );
          dispatch(updateInterval(from.format(), to.format(), 'custom'));
        } else {
          const [from, to] = calcShortcutInterval(DATE_SHORTCUTS.past24Hours);
          dispatch(updateInterval(from.format(), to.format(), DATE_SHORTCUTS.past24Hours));
        }

        Promise.all([initialMetadata(), initialPredefinedMappedEvents()]).then(
          ([initialMetadata, predefinedMappedEvents]) => {
            setMetadata([...initialMetadata]);
            // Predefined Events
            setPredefinedMappedEvents([...predefinedMappedEvents]);
          },
        );

        setInitialized(true);
        setIsLoading(false);
      })
      .catch((err) => {
        setError(err);
        setIsLoading(false);
        throw err;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setInitialized, dispatch]);

  return [initialized, error, customerEnabled, username, isLoading];
};
